/*
 * Publishes each product's category / category2 (already computed by Liquid into
 * the hidden `category` / `category2` inputs on every add-to-cart form — see
 * product-form.liquid, product-card_form.liquid, product-card_form-small.liquid)
 * to any subscribed Shopify Customer Events pixel, since the pixel sandbox has no
 * access to metafields.
 *
 * Re-scans on DOM mutation (debounced) to cover AJAX-rendered content: collection
 * filter re-renders, infinite scroll, recently-viewed/quick-add carousels.
 */
class ProductCategoryAnalyticsBridge {
  constructor() {
    this.published = new Set();

    // Under slow network/CPU conditions the Customer Events pixel sandbox can
    // still be initializing when an early publish fires, and
    // Shopify.analytics.publish has no buffering for subscribers that aren't
    // listening yet — that publish is lost for good. One immediate attempt
    // plus one late attempt (forced, bypassing the dedup set) is enough: only
    // the first attempt that lands *after* the pixel has subscribed matters,
    // not how many attempts happen before it. Confirmed via throttled testing
    // that a single immediate attempt alone isn't enough — TODO: re-tune this
    // 2000ms figure if throttled testing still times out.
    [0, 2000].forEach((delay) => {
      setTimeout(() => this.publish(true), delay);
    });

    this.observe();
  }

  collect(force) {
    const items = [];

    document.querySelectorAll('form[data-productid]').forEach((form) => {
      const productId = form.dataset.productid;
      const category1Input = form.querySelector('input[name="category"]');
      const category2Input = form.querySelector('input[name="category2"]');

      if (!productId) return;
      if (!force && this.published.has(productId)) return;
      if (!category1Input && !category2Input) return;

      items.push({
        productId,
        category1: category1Input?.value || '',
        category2: category2Input?.value || '',
      });
    });

    return items;
  }

  publish(force) {
    if (!window.Shopify?.analytics?.publish) return;

    const items = this.collect(force);
    if (!items.length) return;

    items.forEach((item) => this.published.add(item.productId));

    window.Shopify.analytics.publish('custom_theme_product_category', { items });
  }

  observe() {
    const schedulePublish = debounce(() => this.publish(false), 250);

    const addsProductForm = (nodes) =>
      Array.from(nodes).some(
        (node) => node.nodeType === 1 && (node.matches?.('form[data-productid]') || node.querySelector?.('form[data-productid]'))
      );

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => addsProductForm(mutation.addedNodes))) return;
      schedulePublish();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductCategoryAnalyticsBridge());
} else {
  new ProductCategoryAnalyticsBridge();
}
